export type EnvironmentalMetricId = 'temperature' | 'humidity' | 'emc' | 'dew-point' | 'dew-point-margin';
export type EnvironmentalMetricStatus = 'alarm' | 'warning' | 'expected';
export type EnvironmentalTimeRangeId = '15-minutes' | '1-day' | '1-week' | '1-month' | '3-months';
export type EnvironmentalStressMonitoringWindow = 'DAILY' | 'MONTHLY';
export type EnvironmentalStressMonitoringEmptySections = {
    metrics: boolean;
    notifications: boolean;
    chart: boolean;
};

const TIME_WINDOW_SECONDS: Record<EnvironmentalTimeRangeId, number> = {
    '15-minutes': 900,
    '1-day': 86400,
    '1-week': 604800,
    '1-month': 2592000,
    '3-months': 7776000,
};

export const getEnvironmentalTimeWindowSeconds = (rangeId: EnvironmentalTimeRangeId): number =>
    TIME_WINDOW_SECONDS[rangeId];

export type EnvironmentalMetric = {
    id: EnvironmentalMetricId;
    parameter: string;
    label: string;
    value: string;
    unit: string;
    status: EnvironmentalMetricStatus;
    statusLabel: string;
    warningThreshold: number;
    alarmThreshold: number;
    icon?: {
        web?: { name?: string; family?: string; style?: { color?: string; backgroundColor?: string } };
        mobile?: { name?: string; family?: string; style?: { color?: string; backgroundColor?: string } };
    };
};

export type EnvironmentalNotification = {
    id: string;
    parameter?: string;
    notificationId?: string;
    window?: EnvironmentalStressMonitoringWindow;
    metricId: EnvironmentalMetricId;
    title: string;
    severity: 'alarm' | 'warning';
    value: string;
    unit: string;
    time: string;
    date: string;
    occurredAt?: string | number;
    message: string;
    recommendation: string;
    eventDetails?: {
        average: string;
        integrated: string;
        threshold: string;
    };
};

export type EnvironmentalChartPoint = {
    date: string;
    timestamp: string;
    temperature: number;
    humidity: number;
    emc: number;
    dewPoint: number;
    dewPointMargin: number;
};

export type EnvironmentalMonthlyChartPoint = {
    month: string;
    monthLabel?: string;
    timestamp?: string;
    temperature: number;
    humidity: number;
    emc: number;
    dewPoint: number;
    dewPointMargin: number;
};

export type EnvironmentalTimeRange = {
    id: EnvironmentalTimeRangeId;
    label: string;
};

export type EnvironmentalNotificationHistoryItem = {
    id: string;
    metricId: EnvironmentalMetricId;
    parameter?: string;
    severity: 'alarm' | 'warning' | 'normal' | 'offline';
    title: string;
    time: string;
    date: string;
    description?: string;
    message: string;
    average?: string;
    threshold?: string;
};

export type EnvironmentalStressMonitoringRules = {
    samplingIntervalMinutes: number;
    notificationTriggerConsecutivePoints: number;
    severityMapping: {
        warning: string;
        alarm: string;
    };
};

export type EnvironmentalNotificationCounts = {
    warning: number;
    alarm: number;
};

export type EnvironmentalStressMonitoringData = {
    serialNumber?: string;
    metrics: EnvironmentalMetric[];
    notificationCounts: EnvironmentalNotificationCounts;
    notifications: EnvironmentalNotification[];
    chartPoints: EnvironmentalChartPoint[];
    chartPointsByRange?: Partial<Record<EnvironmentalTimeRangeId, EnvironmentalChartPoint[]>>;
    monthlyChartPoints: EnvironmentalMonthlyChartPoint[];
    timeRanges: EnvironmentalTimeRange[];
    notificationHistory: EnvironmentalNotificationHistoryItem[];
    rules: EnvironmentalStressMonitoringRules;
};

export type EnvironmentalStressMonitoringApiResponse = {
    success: boolean;
    data: EnvironmentalStressMonitoringData;
    requestId?: string;
};

export const getChartPointsForRange = (
    data: EnvironmentalStressMonitoringData,
    rangeId: EnvironmentalTimeRangeId
): EnvironmentalChartPoint[] => data.chartPointsByRange?.[rangeId] ?? data.chartPoints;

export const getEnvironmentalDailyChartScale = (
    values: readonly number[]
): { min: number; max: number; interval: number } => {
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const step = Math.max((max - min) / 5, 0.1);
    const magnitude = 10 ** Math.floor(Math.log10(step));
    const interval =
        (step / magnitude <= 1 ? 1 : step / magnitude <= 2 ? 2 : step / magnitude <= 5 ? 5 : 10) * magnitude;
    return {
        min: Math.floor(min / interval) * interval,
        max: Math.max(interval, (Math.floor(max / interval) + 1) * interval),
        interval,
    };
};
